import { useState } from "react";
import {
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
} from "@datapay-by-datacom/design-system";
import type { Agent, AgentBlueprint } from "../../generated/core-api";
import {
  useAgents,
  useCreateAgent,
  useDeleteAgent,
  useUpdateAgent,
} from "../hooks";
import { AGENT_MODEL_OPTIONS } from "../types";
import {
  areBlueprintModelParamsValid,
  emptyAgentBlueprint,
  formatStopSequences,
  getBlueprintModelParamBadges,
  getBlueprintModelParamIssues,
  parseOptionalBoolean,
  parseOptionalNumber,
  parseReasoningEffort,
  parseStopSequences,
  REASONING_EFFORT_OPTIONS,
  usesGpt5FamilyModel,
} from "../utils/blueprintModelParams";
import { getAgentModelLabel } from "../utils/catalogDisplay";
import { CreateAccordion } from "./CreateAccordion";
import styles from "./AgentCatalog.module.css";

function bumpBlueprintVersion(version: string | undefined): string {
  if (!version) {
    return "1.0.0";
  }
  const match = /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)$/.exec(
    version,
  );
  if (match) {
    const [, major, minor, patch] = match;
    return `${major}.${minor}.${Number(patch) + 1}`;
  }
  return "1.0.0";
}

function AgentCard({ agent }: { agent: Agent }) {
  const update = useUpdateAgent(agent.id);
  const deleteMutation = useDeleteAgent();
  const [isEditing, setIsEditing] = useState(false);
  const [blueprint, setBlueprint] = useState<AgentBlueprint>(agent.blueprint);
  const [hasInvalidNumberInput, setHasInvalidNumberInput] = useState(false);

  const handleSave = () => {
    const nextBlueprint: AgentBlueprint = {
      ...blueprint,
      version: bumpBlueprintVersion(agent.blueprint.version),
    };
    update.mutate(
      { blueprint: nextBlueprint },
      {
        onSuccess: () => {
          setBlueprint(nextBlueprint);
          setIsEditing(false);
        },
      },
    );
  };

  return (
    <div className={`${styles.cardCell} ${isEditing ? styles.cardCellEditing : ""}`}>
      <Card>
        <CardHeader>
          <div className={styles.cardHeader}>
            <h3 className={styles.cardTitle}>{agent.blueprint.name}</h3>
          </div>
        </CardHeader>
        <CardBody>
          {isEditing ? (
            <BlueprintFields
              idPrefix={`edit-${agent.id}`}
              blueprint={blueprint}
              onChange={setBlueprint}
              onInvalidNumberInputChange={setHasInvalidNumberInput}
            />
          ) : (
            <>
              <p className={styles.cardDescription}>
                {agent.blueprint.description.trim() || "No description"}
              </p>
              <div className={styles.metaRow}>
                <span className={styles.badge}>
                  {getAgentModelLabel(agent.blueprint.model_name)}
                </span>
                {agent.blueprint.version && (
                  <span className={styles.badge}>
                    v{agent.blueprint.version}
                  </span>
                )}
                {getBlueprintModelParamBadges(agent.blueprint).map((badge) => (
                  <span key={badge} className={styles.badge}>
                    {badge}
                  </span>
                ))}
              </div>
            </>
          )}
        </CardBody>
        <CardFooter>
          {isEditing ? (
            <ButtonGroup>
              <Button
                onPress={handleSave}
                isDisabled={
                  update.isPending ||
                  hasInvalidNumberInput ||
                  !areBlueprintModelParamsValid(blueprint)
                }
              >
                {update.isPending ? "Saving…" : "Save"}
              </Button>
              <Button variant="secondary" onPress={() => setIsEditing(false)}>
                Cancel
              </Button>
            </ButtonGroup>
          ) : (
            <div className={styles.actions}>
              <Button
                variant="secondary"
                size="small"
                onPress={() => {
                  setBlueprint(agent.blueprint);
                  setHasInvalidNumberInput(false);
                  setIsEditing(true);
                }}
              >
                Edit
              </Button>
              <Button
                variant="secondary"
                size="small"
                onPress={() => deleteMutation.mutate(agent.id)}
                isDisabled={deleteMutation.isPending}
              >
                Delete
              </Button>
            </div>
          )}
        </CardFooter>
      </Card>
    </div>
  );
}

function OptionalNumberField({
  id,
  label,
  hint,
  value,
  min,
  max,
  step,
  onChange,
  onInvalidInputChange,
}: {
  id: string;
  label: string;
  hint?: string;
  value: number | null | undefined;
  min?: number;
  max?: number;
  step?: number;
  onChange: (value: number | undefined) => void;
  onInvalidInputChange: (id: string, isInvalid: boolean) => void;
}) {
  const [rawValue, setRawValue] = useState(value?.toString() ?? "");
  const isInvalid = rawValue.trim() !== "" && parseOptionalNumber(rawValue) === undefined;

  return (
    <div className={styles.fieldRow}>
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type="text"
        inputMode="decimal"
        min={min}
        max={max}
        step={step}
        className={styles.textInput}
        value={rawValue}
        onChange={(e) => {
          const nextRawValue = e.target.value;
          const parsedValue = parseOptionalNumber(nextRawValue);
          const nextIsInvalid =
            nextRawValue.trim() !== "" && parsedValue === undefined;

          setRawValue(nextRawValue);
          onInvalidInputChange(id, nextIsInvalid);
          if (!nextIsInvalid) {
            onChange(parsedValue);
          }
        }}
      />
      {hint ? <p className={styles.fieldHint}>{hint}</p> : null}
      {isInvalid ? (
        <p className={styles.errorText}>Enter a valid number.</p>
      ) : null}
    </div>
  );
}

function ModelParamFields({
  idPrefix,
  blueprint,
  onChange,
  onInvalidNumberInputChange,
}: {
  idPrefix: string;
  blueprint: AgentBlueprint;
  onChange: (blueprint: AgentBlueprint) => void;
  onInvalidNumberInputChange: (hasInvalidInput: boolean) => void;
}) {
  const [stopSequencesText, setStopSequencesText] = useState(() =>
    formatStopSequences(blueprint.stop_sequences),
  );
  const [invalidNumberFieldIds, setInvalidNumberFieldIds] = useState<Set<string>>(
    new Set(),
  );
  const onInvalidInputChange = (id: string, isInvalid: boolean) => {
    setInvalidNumberFieldIds((current) => {
      const next = new Set(current);
      if (isInvalid) {
        next.add(id);
      } else {
        next.delete(id);
      }
      onInvalidNumberInputChange(next.size > 0);
      return next;
    });
  };
  const issues = getBlueprintModelParamIssues(blueprint);
  const showGpt5TemperatureHint =
    usesGpt5FamilyModel(blueprint.model_name) &&
    blueprint.temperature !== undefined &&
    blueprint.temperature !== null;
  const showSamplingConflictHint =
    blueprint.temperature !== undefined &&
    blueprint.temperature !== null &&
    blueprint.top_p !== undefined &&
    blueprint.top_p !== null;

  return (
    <div className={styles.parameterSection}>
      <h4 className={styles.parameterSectionTitle}>Model parameters</h4>
      <p className={styles.parameterHint}>
        Leave knobs blank to use the provider default. Unset values are omitted
        from Foundry calls. GPT-5 family models usually need reasoning effort,
        not temperature.
      </p>
      <div className={styles.fieldRow}>
        <label htmlFor={`${idPrefix}-reasoning-effort`}>
          Reasoning effort (optional)
        </label>
        <select
          id={`${idPrefix}-reasoning-effort`}
          className={styles.select}
          value={blueprint.reasoning_effort ?? ""}
          onChange={(e) =>
            onChange({
              ...blueprint,
              reasoning_effort: parseReasoningEffort(e.target.value),
            })
          }
        >
          <option value="">Provider default</option>
          {REASONING_EFFORT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
      <div className={styles.paramGrid}>
        <OptionalNumberField
          id={`${idPrefix}-temperature`}
          label="Temperature (optional)"
          hint="0–2. Leave blank for GPT-5 family models."
          value={blueprint.temperature}
          min={0}
          max={2}
          step={0.1}
          onChange={(temperature) => onChange({ ...blueprint, temperature })}
          onInvalidInputChange={onInvalidInputChange}
        />
        <OptionalNumberField
          id={`${idPrefix}-top-p`}
          label="top_p (optional)"
          hint="0–1. Prefer this or temperature, not both."
          value={blueprint.top_p}
          min={0}
          max={1}
          step={0.05}
          onChange={(top_p) => onChange({ ...blueprint, top_p })}
          onInvalidInputChange={onInvalidInputChange}
        />
        <OptionalNumberField
          id={`${idPrefix}-max-tokens`}
          label="Max tokens (optional)"
          value={blueprint.max_tokens}
          min={1}
          step={1}
          onChange={(max_tokens) => onChange({ ...blueprint, max_tokens })}
          onInvalidInputChange={onInvalidInputChange}
        />
        <OptionalNumberField
          id={`${idPrefix}-timeout`}
          label="Timeout seconds (optional)"
          hint="Greater than 0, at most 180."
          value={blueprint.timeout}
          min={0.1}
          max={180}
          step={1}
          onChange={(timeout) => onChange({ ...blueprint, timeout })}
          onInvalidInputChange={onInvalidInputChange}
        />
        <OptionalNumberField
          id={`${idPrefix}-frequency-penalty`}
          label="Frequency penalty (optional)"
          value={blueprint.frequency_penalty}
          min={-2}
          max={2}
          step={0.1}
          onChange={(frequency_penalty) =>
            onChange({ ...blueprint, frequency_penalty })
          }
          onInvalidInputChange={onInvalidInputChange}
        />
        <OptionalNumberField
          id={`${idPrefix}-presence-penalty`}
          label="Presence penalty (optional)"
          value={blueprint.presence_penalty}
          min={-2}
          max={2}
          step={0.1}
          onChange={(presence_penalty) =>
            onChange({ ...blueprint, presence_penalty })
          }
          onInvalidInputChange={onInvalidInputChange}
        />
        <OptionalNumberField
          id={`${idPrefix}-seed`}
          label="Seed (optional)"
          value={blueprint.seed}
          step={1}
          onChange={(seed) => onChange({ ...blueprint, seed })}
          onInvalidInputChange={onInvalidInputChange}
        />
      </div>
      <div className={styles.fieldRow}>
        <label htmlFor={`${idPrefix}-parallel-tools`}>
          Parallel tool calls (optional)
        </label>
        <select
          id={`${idPrefix}-parallel-tools`}
          className={styles.select}
          value={
            blueprint.parallel_tool_calls === true
              ? "true"
              : blueprint.parallel_tool_calls === false
                ? "false"
                : ""
          }
          onChange={(e) =>
            onChange({
              ...blueprint,
              parallel_tool_calls: parseOptionalBoolean(e.target.value),
            })
          }
        >
          <option value="">Provider default</option>
          <option value="true">Enabled</option>
          <option value="false">Disabled</option>
        </select>
      </div>
      <div className={styles.fieldRow}>
        <label htmlFor={`${idPrefix}-stop-sequences`}>
          Stop sequences (optional)
        </label>
        <textarea
          id={`${idPrefix}-stop-sequences`}
          className={`${styles.textArea} ${styles.parameterTextArea}`}
          value={stopSequencesText}
          onChange={(e) => {
            setStopSequencesText(e.target.value);
            onChange({
              ...blueprint,
              stop_sequences: parseStopSequences(e.target.value),
            });
          }}
        />
        <p className={styles.fieldHint}>
          One sequence per line. At most 4 sequences, 64 characters each.
        </p>
      </div>
      {showGpt5TemperatureHint && (
        <p className={styles.warningText}>
          GPT-5 family deployments often reject a custom temperature. Prefer
          reasoning effort.
        </p>
      )}
      {showSamplingConflictHint && (
        <p className={styles.warningText}>
          Some models reject temperature and top_p together. Leave one blank.
        </p>
      )}
      {invalidNumberFieldIds.size > 0 ? (
        <p className={styles.errorText}>Correct invalid number fields before saving.</p>
      ) : null}
      {issues.map((issue) => (
        <p key={`${issue.field}-${issue.message}`} className={styles.errorText}>
          {issue.message}
        </p>
      ))}
    </div>
  );
}

function BlueprintFields({
  idPrefix,
  blueprint,
  onChange,
  onInvalidNumberInputChange,
}: {
  idPrefix: string;
  blueprint: AgentBlueprint;
  onChange: (blueprint: AgentBlueprint) => void;
  onInvalidNumberInputChange: (hasInvalidInput: boolean) => void;
}) {
  return (
    <>
      <div className={styles.fieldRow}>
        <label htmlFor={`${idPrefix}-name`}>Name</label>
        <input
          id={`${idPrefix}-name`}
          className={styles.textInput}
          value={blueprint.name}
          onChange={(e) => onChange({ ...blueprint, name: e.target.value })}
        />
      </div>
      <div className={styles.fieldRow}>
        <label htmlFor={`${idPrefix}-description`}>Description</label>
        <input
          id={`${idPrefix}-description`}
          className={styles.textInput}
          value={blueprint.description}
          onChange={(e) =>
            onChange({ ...blueprint, description: e.target.value })
          }
        />
      </div>
      <div className={styles.fieldRow}>
        <label htmlFor={`${idPrefix}-prompt`}>System prompt template</label>
        <textarea
          id={`${idPrefix}-prompt`}
          className={styles.textArea}
          value={blueprint.system_prompt_template}
          onChange={(e) =>
            onChange({ ...blueprint, system_prompt_template: e.target.value })
          }
        />
      </div>
      <div className={styles.fieldRow}>
        <label htmlFor={`${idPrefix}-model`}>Model</label>
        <select
          id={`${idPrefix}-model`}
          className={styles.select}
          value={blueprint.model_name}
          onChange={(e) =>
            onChange({ ...blueprint, model_name: e.target.value })
          }
        >
          {AGENT_MODEL_OPTIONS.map((model) => (
            <option
              key={model.value}
              value={model.value}
              disabled={!model.enabled}
            >
              {model.label}
              {!model.enabled ? " (coming soon)" : ""}
            </option>
          ))}
        </select>
      </div>
      <ModelParamFields
        idPrefix={idPrefix}
        blueprint={blueprint}
        onChange={onChange}
        onInvalidNumberInputChange={onInvalidNumberInputChange}
      />
    </>
  );
}

export function AgentCatalogSection() {
  const createMutation = useCreateAgent();
  const agentsQuery = useAgents();
  const [createOpen, setCreateOpen] = useState(false);
  const [hasInvalidCreateNumberInput, setHasInvalidCreateNumberInput] =
    useState(false);

  const enabledModel =
    AGENT_MODEL_OPTIONS.find((m) => m.enabled)?.value ?? "gpt-5.4-mini";

  const [blueprint, setBlueprint] = useState<AgentBlueprint>(() =>
    emptyAgentBlueprint(enabledModel),
  );

  const resetBlueprint = () => {
    setBlueprint(emptyAgentBlueprint(enabledModel));
    setHasInvalidCreateNumberInput(false);
  };

  const handleCreate = () => {
    createMutation.mutate(
      { blueprint },
      {
        onSuccess: () => {
          resetBlueprint();
          setCreateOpen(false);
        },
      },
    );
  };

  const agents = agentsQuery.data ?? [];

  return (
    <section className={styles.section}>
      <p className={styles.sectionDescription}>
        Platform-level agent blueprints. Organisation workspaces attach service
        accounts to these agents.
      </p>

      <CreateAccordion
        title="New agent"
        isOpen={createOpen}
        onToggle={() => setCreateOpen((open) => !open)}
      >
        <BlueprintFields
          idPrefix="create-agent"
          blueprint={blueprint}
          onChange={setBlueprint}
          onInvalidNumberInputChange={setHasInvalidCreateNumberInput}
        />
        <ButtonGroup>
          <Button
            onPress={handleCreate}
            isDisabled={
              createMutation.isPending ||
              !blueprint.name.trim() ||
              !blueprint.system_prompt_template.trim() ||
              hasInvalidCreateNumberInput ||
              !areBlueprintModelParamsValid(blueprint)
            }
          >
            {createMutation.isPending ? "Creating…" : "Create agent"}
          </Button>
        </ButtonGroup>
        {createMutation.isError && (
          <p className={styles.errorText}>Failed to create agent.</p>
        )}
      </CreateAccordion>

      {agentsQuery.isLoading && <p>Loading agents…</p>}
      {agentsQuery.isError && (
        <p className={styles.errorText}>Failed to load agents.</p>
      )}
      {agentsQuery.data && agents.length === 0 && (
        <p className={styles.emptyState}>
          No agents yet. Open New agent to add a blueprint.
        </p>
      )}
      {agents.length > 0 && (
        <div className={styles.cardGrid}>
          {agents.map((agent) => (
            <AgentCard key={agent.id} agent={agent} />
          ))}
        </div>
      )}
    </section>
  );
}
